import { useEffect, useRef, useState, type InputHTMLAttributes, type ReactNode, type Ref } from 'react';
import { Icon } from './icons';

/**
 * Champs du design system (ADR-092) : champ **effaçable** (une croix à droite dès qu'il contient du
 * texte : elle vide le champ et y rend le focus) et champ **mot de passe** (un œil bascule
 * l'affichage ; la valeur n'est jamais journalisée ni mémorisée, et repasse masquée à la soumission
 * du formulaire). Présentation seulement : la valeur reste celle du composant appelant.
 */

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string;
  onValueChange: (value: string) => void;
  ref?: Ref<HTMLInputElement>;
};

function assignRef(ref: Ref<HTMLInputElement> | undefined, element: HTMLInputElement | null): void {
  if (typeof ref === 'function') ref(element);
  else if (ref !== undefined && ref !== null) (ref as { current: HTMLInputElement | null }).current = element;
}

export function ClearableInput({ value, onValueChange, ref, clearLabel = 'Effacer', className, ...rest }: InputProps & { clearLabel?: string }) {
  const input = useRef<HTMLInputElement | null>(null);
  const filled = value !== '' && rest.disabled !== true && rest.readOnly !== true;
  return (
    <span className={`ay-input-adorned${className === undefined ? '' : ` ${className}`}`}>
      <input
        {...rest}
        ref={(element) => {
          input.current = element;
          assignRef(ref, element);
        }}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
      />
      {filled && (
        <button
          type="button"
          className="ay-input-adorned__button"
          aria-label={clearLabel}
          title={clearLabel}
          onClick={() => {
            onValueChange('');
            input.current?.focus();
          }}
        >
          <Icon name="close" size={16} />
        </button>
      )}
    </span>
  );
}

/**
 * Bouton d'envoi du design system : libellé centré, état occupé (indicateur, bouton désactivé,
 * `aria-busy`, libellé d'attente) pendant la requête.
 */
export function SubmitButton({ busy, busyLabel, children, variant = 'block', className }: { busy: boolean; busyLabel: string; children: ReactNode; variant?: 'block' | 'inline'; className?: string }) {
  const classes = ['ay-button', 'ay-button--lg', ...(variant === 'block' ? ['ay-button--block'] : []), ...(className === undefined ? [] : [className])].join(' ');
  return (
    <button type="submit" className={classes} disabled={busy} aria-busy={busy}>
      {busy && <span className="ay-spinner" aria-hidden="true" />}
      <span>{busy ? busyLabel : children}</span>
    </button>
  );
}

export function PasswordInput({
  value,
  onValueChange,
  ref,
  showLabel = 'Afficher le mot de passe',
  hideLabel = 'Masquer le mot de passe',
  className,
  ...rest
}: InputProps & { showLabel?: string; hideLabel?: string }) {
  const [visible, setVisible] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  // La soumission du formulaire masque de nouveau le mot de passe.
  useEffect(() => {
    const form = input.current?.form;
    if (form === null || form === undefined) return;
    const hide = (): void => setVisible(false);
    form.addEventListener('submit', hide);
    return () => form.removeEventListener('submit', hide);
  }, []);
  return (
    <span className={`ay-input-adorned${className === undefined ? '' : ` ${className}`}`}>
      <input
        {...rest}
        ref={(element) => {
          input.current = element;
          assignRef(ref, element);
        }}
        type={visible ? 'text' : 'password'}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
      />
      <button type="button" className="ay-input-adorned__button" aria-label={visible ? hideLabel : showLabel} title={visible ? hideLabel : showLabel} aria-pressed={visible} onClick={() => setVisible(!visible)}>
        <Icon name={visible ? 'eye-off' : 'eye'} size={16} />
      </button>
    </span>
  );
}
